import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import MultiImageUpload from "@/components/MultiImageUpload";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = {
  agency_id: "", name: "", brand: "", model: "", year: 2024, category: "SUV",
  transmission: "Automatique", fuel: "Essence", seats: 5, doors: 4, ac: true,
  mileage: 0, color: "", price_per_day: 0, deposit_percent: 30, deposit_mode: "percent", deposit_fixed: 0, photos: [],
  description: "", features: [], with_driver_available: true, without_driver_available: true,
  driver_price_per_day: 0, conditions: "", available: true, unavailable_dates: [],
  rental_types: ["day"], min_duration_days: 1, badges: [],
  driver_languages: [], driver_experience_years: 0, driver_availability: "",
};

const BADGE_OPTIONS = [
  { key: "airport_delivery", label: "Livraison aéroport" },
  { key: "hotel_delivery", label: "Livraison hôtel" },
  { key: "instant_booking", label: "Réservation instantanée" },
  { key: "insurance_included", label: "Assurance incluse" },
  { key: "premium", label: "Premium" },
];

export default function AdminVehicles() {
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [featuresStr, setFeaturesStr] = useState("");

  const { user } = useAuth();
  const load = async () => {
    let vehicles = (await api.get("/vehicles")).data;
    let ags = (await api.get("/agencies")).data;
    // Cloisonnement: an agency_partner ne voit et ne gère QUE ses propres véhicules
    if (user?.role === "agency_partner") {
      const myAgency = ags.find((a) => a.owner_user_id === user.id);
      const myId = myAgency?.id;
      ags = myAgency ? [myAgency] : [];
      vehicles = myId ? vehicles.filter((v) => v.agency_id === myId) : [];
    }
    setItems(vehicles); setAgencies(ags);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditId(null); setForm(empty); setFeaturesStr(""); setOpen(true); };
  const edit = (v) => {
    setEditId(v.id); setForm({ ...empty, ...v });
    setFeaturesStr((v.features || []).join(", "));
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        features: featuresStr.split(",").map((s) => s.trim()).filter(Boolean),
        year: parseInt(form.year), seats: parseInt(form.seats), doors: parseInt(form.doors) || 4,
        mileage: parseInt(form.mileage) || 0, price_per_day: parseFloat(form.price_per_day),
        deposit_percent: parseFloat(form.deposit_percent) || 30,
        deposit_fixed: parseFloat(form.deposit_fixed) || 0,
        deposit_mode: form.deposit_mode || "percent",
        driver_price_per_day: parseFloat(form.driver_price_per_day) || 0,
      };
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Véhicule enregistré"); setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce véhicule ?")) return; await api.delete(`/vehicles/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Véhicules</h1>
          <p className="text-sm text-gray-500 mt-1">Gérez la flotte des agences partenaires.</p>
        </div>
        <Button data-testid="add-vehicle-btn" onClick={openNew} className="mbk-btn-green text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Nouveau véhicule</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0a1e3f]">{editId ? "Modifier" : "Ajouter"} un véhicule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <F l="Agence *">
              <Select value={form.agency_id} onValueChange={(v) => upd("agency_id", v)}>
                <SelectTrigger data-testid="v-agency"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.city})</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F l="Nom / titre"><Input data-testid="v-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Toyota RAV4" /></F>
            <F l="Marque"><Input value={form.brand} onChange={(e) => upd("brand", e.target.value)} /></F>
            <F l="Modèle"><Input value={form.model} onChange={(e) => upd("model", e.target.value)} /></F>
            <F l="Année"><Input type="number" value={form.year} onChange={(e) => upd("year", e.target.value)} /></F>
            <F l="Catégorie">
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["SUV", "Berline", "Pick-up", "Minibus", "4x4", "Citadine"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F l="Transmission">
              <Select value={form.transmission} onValueChange={(v) => upd("transmission", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Automatique">Automatique</SelectItem><SelectItem value="Manuelle">Manuelle</SelectItem></SelectContent>
              </Select>
            </F>
            <F l="Carburant">
              <Select value={form.fuel} onValueChange={(v) => upd("fuel", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Essence", "Diesel", "Hybride", "Électrique"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F l="Places"><Input type="number" value={form.seats} onChange={(e) => upd("seats", e.target.value)} /></F>
            <F l="Portes"><Input type="number" value={form.doors} onChange={(e) => upd("doors", e.target.value)} /></F>
            <F l="Couleur"><Input value={form.color} onChange={(e) => upd("color", e.target.value)} /></F>
            <F l="Kilométrage"><Input type="number" value={form.mileage} onChange={(e) => upd("mileage", e.target.value)} /></F>
            <F l="Prix / jour (XAF) *"><Input data-testid="v-price" type="number" value={form.price_per_day} onChange={(e) => upd("price_per_day", e.target.value)} /></F>
            <F l="Type d'acompte">
              <Select value={form.deposit_mode || "percent"} onValueChange={(v) => upd("deposit_mode", v)}>
                <SelectTrigger data-testid="v-deposit-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Pourcentage (%)</SelectItem>
                  <SelectItem value="fixed">Montant fixe (XAF)</SelectItem>
                </SelectContent>
              </Select>
            </F>
            {form.deposit_mode === "fixed" ? (
              <F l="Acompte fixe (XAF)"><Input data-testid="v-deposit-fixed" type="number" value={form.deposit_fixed} onChange={(e) => upd("deposit_fixed", e.target.value)} placeholder="ex : 65000" /></F>
            ) : (
              <F l="Acompte (%)"><Input data-testid="v-deposit-percent" type="number" value={form.deposit_percent} onChange={(e) => upd("deposit_percent", e.target.value)} placeholder="ex : 30" /></F>
            )}
            <F l="Supplément chauffeur / jour"><Input type="number" value={form.driver_price_per_day} onChange={(e) => upd("driver_price_per_day", e.target.value)} /></F>
            <div className="flex items-center gap-3 mt-6"><Switch checked={form.ac} onCheckedChange={(v) => upd("ac", v)} /> <span className="text-sm">Climatisation</span></div>
            <div className="flex items-center gap-3 mt-6"><Switch checked={form.available} onCheckedChange={(v) => upd("available", v)} /> <span className="text-sm">Disponible</span></div>
            <div className="md:col-span-2"><F l="Photos du véhicule (téléchargez depuis votre appareil)"><MultiImageUpload value={form.photos} onChange={(v) => upd("photos", v)} testId="veh-photos" /></F></div>
            <div className="md:col-span-2"><F l="Équipements (séparés par des virgules)"><Input value={featuresStr} onChange={(e) => setFeaturesStr(e.target.value)} placeholder="GPS, Bluetooth, Caméra recul" /></F></div>
            <div className="md:col-span-2"><F l="Description"><Textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} /></F></div>
            <div className="md:col-span-2"><F l="Conditions de location"><Textarea rows={2} value={form.conditions} onChange={(e) => upd("conditions", e.target.value)} /></F></div>

            <div className="md:col-span-2 border-t pt-4 mt-2">
              <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">Options de service</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3"><Switch checked={form.with_driver_available} onCheckedChange={(v) => upd("with_driver_available", v)} /><span className="text-sm">Avec chauffeur disponible</span></div>
                <div className="flex items-center gap-3"><Switch checked={form.without_driver_available} onCheckedChange={(v) => upd("without_driver_available", v)} /><span className="text-sm">Sans chauffeur disponible</span></div>
                <F l="Durée min. de location (jours)"><Input type="number" min="1" value={form.min_duration_days} onChange={(e) => upd("min_duration_days", parseInt(e.target.value) || 1)} /></F>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Types de location proposés</Label>
                  <div className="flex gap-4 mt-2">
                    {[["day","Jour"],["week","Semaine"],["month","Mois"]].map(([k,l]) => (
                      <label key={k} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={(form.rental_types || []).includes(k)} onChange={(e) => {
                          const arr = new Set(form.rental_types || []);
                          e.target.checked ? arr.add(k) : arr.delete(k);
                          upd("rental_types", Array.from(arr));
                        }} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {form.with_driver_available && (
              <div className="md:col-span-2 border-t pt-4">
                <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">Informations chauffeur</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F l="Langues parlées (séparées par des virgules)"><Input value={(form.driver_languages || []).join(", ")} onChange={(e) => upd("driver_languages", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Français, English, Lingala" /></F>
                  <F l="Années d'expérience"><Input type="number" min="0" value={form.driver_experience_years} onChange={(e) => upd("driver_experience_years", parseInt(e.target.value) || 0)} /></F>
                  <div className="md:col-span-2"><F l="Horaires de disponibilité"><Input value={form.driver_availability} onChange={(e) => upd("driver_availability", e.target.value)} placeholder="Lun-Dim 6h-22h" /></F></div>
                </div>
              </div>
            )}

            <div className="md:col-span-2 border-t pt-4">
              <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">Badges affichés sur l'annonce</div>
              <div className="flex flex-wrap gap-3">
                {BADGE_OPTIONS.map((b) => (
                  <label key={b.key} className="flex items-center gap-1.5 text-sm bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={(form.badges || []).includes(b.key)} onChange={(e) => {
                      const arr = new Set(form.badges || []);
                      e.target.checked ? arr.add(b.key) : arr.delete(b.key);
                      upd("badges", Array.from(arr));
                    }} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-vehicle" onClick={save} className="mbk-btn-primary text-white">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-2xl border border-gray-100 mbk-card-shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left p-4">Véhicule</th>
              <th className="text-left p-4">Agence</th>
              <th className="text-left p-4">Prix/jour</th>
              <th className="text-left p-4">Statut</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={5} className="p-12 text-center text-gray-400">Aucun véhicule.</td></tr> :
              items.map((v) => (
                <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={(v.photos && v.photos[0]) || ""} className="h-12 w-16 object-cover rounded-lg bg-gray-100" alt="" />
                      <div><div className="font-semibold text-[#0a1e3f]">{v.name}</div><div className="text-xs text-gray-500">{v.brand} {v.model} · {v.year}</div></div>
                    </div>
                  </td>
                  <td className="p-4">{v.agency?.name}</td>
                  <td className="p-4 font-bold text-[#0a1e3f]">{fmt(v.price_per_day)}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full font-bold ${v.available ? "bg-[#dcfce7] text-[#0e7a3f]" : "bg-gray-100 text-gray-500"}`}>{v.available ? "Disponible" : "Indisponible"}</span></td>
                  <td className="p-4 text-right">
                    <button onClick={() => edit(v)} className="h-8 w-8 rounded-full bg-gray-100 mr-1"><Pencil className="h-3.5 w-3.5 mx-auto" /></button>
                    <button onClick={() => remove(v.id)} className="h-8 w-8 rounded-full bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5 mx-auto" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{l}</Label><div className="mt-1.5">{children}</div></div>);
